import { observer } from 'mobx-react-lite'
import React, { useContext, useMemo, useState } from 'react'
import { CheckCircle, Files, Trash } from 'react-bootstrap-icons'
import { useParams } from 'react-router-dom'
import { CampaignContext } from '../../../../../contexts/CampaignContext'
import Switch from '../../../../FormComponents/Switch'
import DeleteBudgetRowModal from './DeleteBudgetRowModal'
import { slotContentPieceCount } from './BudgetDetailsPanelRowBuilders'
import {
  isInfluencerVisibleInBudgetScenario,
  setInfluencerVisibleInBudgetForScenario,
} from './influencerBudgetVisibility'

export default observer(function BudgetTableRow({
  renderedColumnDefs,
  row,
  rowIndex,
  editingColumns,
  draftColumns,
  onInputCellChange,
  tableKind,
  campaignScenario,
  showInfluencerVisibilityToggle,
  onDuplicateRow,
  onDeleteRow,
}) {
  const { id: campaignId } = useParams()
  const scenarioId = campaignScenario?.id
  const { campaign, authentications, influencers, campaignState } = useContext(CampaignContext)

  const rowInfluencer = useMemo(
    () => (row?.influencerId != null ? influencers?.find(i => Number(i.id) === Number(row.influencerId)) : null),
    [influencers, row?.influencerId]
  )

  const influencerPageAuthId =
    row?.authenticationId ?? rowInfluencer?.authentications?.[0]?.id ?? null

  const rowSlotNumber = Number(row?.slotNumber) || 1

  const isVisibleInBudget =
    rowInfluencer && campaignScenario
      ? isInfluencerVisibleInBudgetScenario(campaignScenario, rowInfluencer, campaign, authentications, rowSlotNumber)
      : true

  const toggleVisibleInBudget = event => {
    if (!rowInfluencer || !campaignScenario) return
    const newValue = event?.target?.checked ?? !isVisibleInBudget
    setInfluencerVisibleInBudgetForScenario({
      campaignId,
      scenarioId,
      campaignScenario,
      influencer: rowInfluencer,
      slotNumber: rowSlotNumber,
      newValue,
      campaignState,
    }).catch(() => {})
  }

  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const isExtraRow = rowSlotNumber > 1
  const contentPieceCount = campaignScenario ? slotContentPieceCount(campaignScenario, row?.influencerId, rowSlotNumber) : 0

  const confirmDeleteRow = () => {
    setShowDeleteModal(false)
    onDeleteRow(row.influencerId, rowSlotNumber)
  }

  return (
    <>
    <tr className="border-b border-gray-100 dark:border-gray-800 last:border-b-0">
      {renderedColumnDefs.map(col => {
        const isDimmed = editingColumns && !draftColumns[col.key]
        const isAuthMissingCell = !!row?._disabledCells?.[col.key]
        const isLockedCell = !!row?._lockedCells?.[col.key]
        const showInput = col.input && onInputCellChange && !isAuthMissingCell && !isLockedCell
        const leftAlign = col.key === 'influencer' || col.key === 'instagram'
        const baseTd = `py-3 px-2 border border-gray-200 text-center ${col.dark ? 'bg-gray-800 text-gray-200' : 'text-gray-700 dark:text-gray-300'} ${leftAlign ? 'text-left' : ''} ${isAuthMissingCell && !col.dark ? 'bg-gray-50 text-gray-400 dark:bg-rb-dark-mode-lighter dark:text-gray-500' : ''} ${isDimmed ? 'opacity-40' : ''}`

        if (showInput) {
          return (
            <td key={`${rowIndex}-${col.key}`} className={baseTd}>
              <input
                type="text"
                value={row[col.key] ?? ''}
                disabled={isDimmed}
                onChange={e => onInputCellChange(rowIndex, col.key, e.target.value)}
                className="w-full min-w-[2.5rem] mx-auto rounded border-0 bg-transparent px-1 py-0.5 text-center text-xs text-gray-900 outline-none ring-0 focus:border-0 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white"
              />
            </td>
          )
        }

        if (isLockedCell) {
          return (
            <td
              key={`${rowIndex}-${col.key}`}
              className={baseTd}
              title="This channel is managed by Modash"
            >
              <div className={`flex items-center gap-1 ${leftAlign ? 'justify-start' : 'justify-center'}`}>
                <span className="truncate">{row[col.key] ?? ''}</span>
                <span className="inline-flex shrink-0 items-center text-xs text-rb-green">
                  <CheckCircle />
                </span>
              </div>
            </td>
          )
        }

        if (isAuthMissingCell) {
          const buyoutNoContentTitle =
            tableKind === 'buyouts' && row?._noMediaActions
              ? 'Add content pieces in Overview first (e.g. Reels, Posts)'
              : 'No matching channel authentication for this influencer'
          return (
            <td
              key={`${rowIndex}-${col.key}`}
              className={baseTd}
              title={buyoutNoContentTitle}
            >
              {tableKind === 'buyouts' && row?._noMediaActions ? '—' : '-'}
            </td>
          )
        }

        if (col.key === 'avatar' && row.avatarUrl) {
          return (
            <td key={`${rowIndex}-${col.key}`} className={baseTd}>
              <img src={row.avatarUrl} className="h-7 w-7 rounded-full object-cover mx-auto" alt="" />
            </td>
          )
        }

        if (
          col.key === 'influencer' &&
          (tableKind === 'overview' || tableKind === 'reelsLike' || tableKind === 'custom' || tableKind === 'overallKosten' || tableKind === 'buyouts') &&
          row.avatarUrl
        ) {
          return (
            <td key={`${rowIndex}-${col.key}`} className={baseTd}>
              <div className="flex items-center gap-2 justify-start">
                {showInfluencerVisibilityToggle && rowInfluencer && (
                  <div className="shrink-0" onClick={e => e.stopPropagation()}>
                    <Switch name={`visible_in_budget_${row.influencerId}_${rowSlotNumber}`} value={isVisibleInBudget} onChange={toggleVisibleInBudget} />
                  </div>
                )}
                <img src={row.avatarUrl} className="h-7 w-7 shrink-0 rounded-full object-cover" alt="" />
                {influencerPageAuthId ? (
                  <a
                    href={`/project_manager/authentications/${influencerPageAuthId}/influencer/overview`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {row.influencer ?? ''}
                  </a>
                ) : (
                  <span>{row.influencer ?? ''}</span>
                )}
                {onDuplicateRow && rowInfluencer && (
                  <button
                    type="button"
                    title="Duplicate row"
                    onClick={e => {
                      e.stopPropagation()
                      onDuplicateRow(row.influencerId)
                    }}
                    className="shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-rb-green dark:text-gray-500 dark:hover:bg-rb-dark-mode-lighter dark:hover:text-rb-green"
                  >
                    <Files className="text-sm" />
                  </button>
                )}
                {onDeleteRow && rowInfluencer && isExtraRow && (
                  <button
                    type="button"
                    title="Delete row"
                    onClick={e => {
                      e.stopPropagation()
                      setShowDeleteModal(true)
                    }}
                    className="shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:text-gray-500 dark:hover:bg-rb-dark-mode-lighter dark:hover:text-red-400"
                  >
                    <Trash className="text-sm" />
                  </button>
                )}
              </div>
            </td>
          )
        }

        return (
          <td key={`${rowIndex}-${col.key}`} className={baseTd}>
            {row[col.key] ?? ''}
          </td>
        )
      })}
    </tr>
    {onDeleteRow && isExtraRow && (
      <DeleteBudgetRowModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteRow}
        influencerName={row?.influencer ?? ''}
        contentPieceCount={contentPieceCount}
      />
    )}
    </>
  )
})
